import React, { useState, useEffect, useCallback } from 'react';
import { scheduleApi } from '../services/api';
import { Schedule } from '../types';
import ScheduleGrid from '../components/Schedule/ScheduleGrid';
import DateRangePicker from '../components/Schedule/DateRangePicker';
import ScheduleCreationForm from '../components/Schedule/ScheduleCreationForm';

interface DateRange {
  startDate: Date;
  endDate: Date;
}

const Schedules: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: new Date(),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // Next week
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [scheduleToEdit, setScheduleToEdit] = useState<Schedule | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<Schedule | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadSchedules = useCallback(async () => {
    setLoading(true);
    setError(null);

    const startDateStr = dateRange.startDate.toISOString().split('T')[0];
    const endDateStr = dateRange.endDate.toISOString().split('T')[0];
    
    console.log('Loading schedules with params:', { startDate: startDateStr, endDate: endDateStr });

    try {
      const response = await scheduleApi.getAll({ 
        startDate: startDateStr,
        endDate: endDateStr,
        limit: 50 
      });
      
      console.log('Schedules API response:', response);
      const scheduleList = response.data.schedules;
      console.log('Schedule list:', scheduleList);
      
      setSchedules(scheduleList);
      
      // Auto-select the first schedule if available and no schedule is currently selected
      if (scheduleList.length > 0 && !selectedSchedule) {
        console.log('Auto-selecting first schedule:', scheduleList[0]);
        setSelectedSchedule(scheduleList[0]);
      } else if (scheduleList.length === 0) {
        console.log('No schedules found, clearing selection');
        setSelectedSchedule(null);
      }
    } catch (err: any) {
      console.error('Error loading schedules:', err);
      setError(err.error || 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedSchedule]);

  useEffect(() => {
    console.log('Schedules page useEffect triggered with dateRange:', dateRange);
    loadSchedules();
  }, [dateRange, loadSchedules]);

  const handleDateRangeChange = (newRange: DateRange) => {
    setDateRange(newRange);
    setSelectedSchedule(null); // Reset selected schedule when date range changes
  };

  const handleCreateSchedule = (newSchedule: Schedule) => {
    console.log('New schedule created:', newSchedule);
    
    // Update the date range to include the new schedule dates
    const scheduleStartDate = new Date(newSchedule.weekStartDate);
    const scheduleEndDate = new Date(newSchedule.weekEndDate);
    
    console.log('Schedule dates:', { start: scheduleStartDate, end: scheduleEndDate });
    console.log('Current date range:', dateRange);
    
    // Expand date range if necessary to include the new schedule
    const newStartDate = scheduleStartDate < dateRange.startDate ? scheduleStartDate : dateRange.startDate;
    const newEndDate = scheduleEndDate > dateRange.endDate ? scheduleEndDate : dateRange.endDate;
    
    if (newStartDate !== dateRange.startDate || newEndDate !== dateRange.endDate) {
      console.log('Updating date range to include new schedule:', { start: newStartDate, end: newEndDate });
      setDateRange({ startDate: newStartDate, endDate: newEndDate });
    }
    
    setSchedules(prev => [newSchedule, ...prev]);
    setSelectedSchedule(newSchedule);
    setShowCreateForm(false);
    
    // Refresh the list after a short delay to ensure date range is updated
    setTimeout(() => {
      loadSchedules();
    }, 100);
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
  };

  const handleEditSchedule = async (schedule: Schedule) => {
    try {
      // Fetch the complete schedule data with assignments
      const response = await scheduleApi.getById(schedule.id);
      const fullScheduleData = response.data.schedule;

      console.log('Full schedule data for editing:', fullScheduleData);

      setScheduleToEdit(fullScheduleData);
      setShowEditForm(true);
    } catch (error) {
      console.error('Error loading schedule for editing:', error);
      setError('Failed to load schedule details for editing');
    }
  };

  const handleUpdateSchedule = async (updatedSchedule: Schedule) => {
    console.log('Schedule updated:', updatedSchedule);

    // Update the schedules list
    setSchedules(prev => prev.map(s => s.id === updatedSchedule.id ? updatedSchedule : s));

    // Update selected schedule if it's the one being edited
    if (selectedSchedule?.id === updatedSchedule.id) {
      setSelectedSchedule(updatedSchedule);
    }

    setShowEditForm(false);
    setScheduleToEdit(null);

    // Refresh the list to ensure data consistency
    setTimeout(() => {
      loadSchedules();
    }, 100);
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setScheduleToEdit(null);
  };

  const handleDeleteSchedule = (schedule: Schedule) => {
    setScheduleToDelete(schedule);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteSchedule = async () => {
    if (!scheduleToDelete) return;

    setDeleting(true);
    try {
      await scheduleApi.delete(scheduleToDelete.id);

      // Remove from schedules list
      setSchedules(prev => prev.filter(s => s.id !== scheduleToDelete.id));

      // Clear selection if deleted schedule was selected
      if (selectedSchedule?.id === scheduleToDelete.id) {
        setSelectedSchedule(null);
      }

      setShowDeleteConfirm(false);
      setScheduleToDelete(null);

      console.log('Schedule deleted successfully');
    } catch (err: any) {
      console.error('Error deleting schedule:', err);
      setError(err.error || 'Failed to delete schedule');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteSchedule = () => {
    setShowDeleteConfirm(false);
    setScheduleToDelete(null);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '1rem'
        }}></div>
        <p>Loading schedules...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '2rem',
        color: '#e74c3c'
      }}>
        <p>Error: {error}</p>
        <button 
          onClick={loadSchedules}
          style={{
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '1rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Date Range Picker */}
      <DateRangePicker
        selectedRange={dateRange}
        onRangeChange={handleDateRangeChange}
      />

      {schedules.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem',
          color: '#7f8c8d'
        }}>
          <h3>No Schedules Found</h3>
          <p>No schedules found for the selected date range. Try selecting a different date range or create a new schedule.</p>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Create Schedule
          </button>
        </div>
      ) : (
        <>
          <div style={{
            padding: '1rem',
            backgroundColor: 'white',
            marginBottom: '1rem',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                Select Schedule:
              </label>
              <select
                value={selectedSchedule?.id || ''}
                onChange={(e) => {
                  console.log('Schedule selection changed to:', e.target.value);
                  const schedule = schedules.find(s => s.id === parseInt(e.target.value));
                  console.log('Found schedule:', schedule);
                  setSelectedSchedule(schedule || null);
                }}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  minWidth: '300px'
                }}
              >
                <option value="">Select a schedule...</option>
                {schedules.map(schedule => (
                  <option key={schedule.id} value={schedule.id}>
                    {schedule.therapistName} - {new Date(schedule.weekStartDate).toLocaleDateString()} to {new Date(schedule.weekEndDate).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {selectedSchedule && (
                <>
                  <button
                    onClick={() => handleEditSchedule(selectedSchedule)}
                    style={{
                      backgroundColor: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                    title="Edit selected schedule"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteSchedule(selectedSchedule)}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                    title="Delete selected schedule"
                  >
                    Delete
                  </button>
                </>
              )}
              <button
                onClick={() => setShowCreateForm(true)}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Create New Schedule
              </button>
            </div>
          </div>
          
          {selectedSchedule && (
            <ScheduleGrid 
              scheduleId={selectedSchedule.id}
              startDate={dateRange.startDate.toISOString().split('T')[0]}
              endDate={dateRange.endDate.toISOString().split('T')[0]}
            />
          )}
        </>
      )}

      {/* Schedule Creation Form */}
      {showCreateForm && (
        <ScheduleCreationForm
          onSubmit={handleCreateSchedule}
          onCancel={handleCancelCreate}
        />
      )}

      {/* Schedule Edit Form */}
      {showEditForm && scheduleToEdit && (
        <ScheduleCreationForm
          existingSchedule={scheduleToEdit}
          onSubmit={handleUpdateSchedule}
          onCancel={handleCancelEdit}
          isEditMode={true}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && scheduleToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{
              marginTop: 0,
              marginBottom: '1rem',
              color: '#dc3545'
            }}>
              Confirm Delete Schedule
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Are you sure you want to delete the schedule for <strong>{scheduleToDelete.therapistName}</strong>
              ({new Date(scheduleToDelete.weekStartDate).toLocaleDateString()} - {new Date(scheduleToDelete.weekEndDate).toLocaleDateString()})?
            </p>
            <p style={{
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              color: '#6c757d'
            }}>
              This action cannot be undone. All assignments in this schedule will also be removed.
            </p>
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={cancelDeleteSchedule}
                disabled={deleting}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteSchedule}
                disabled={deleting}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                {deleting ? 'Deleting...' : 'Delete Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedules;